import "../index.css";
import { useEffect } from "react";
import Footer from "../components/Footer";
//import { useAuth } from "../auth/context";
import { PageNav } from "../components/PageNav";
import ReactMarkdown from "react-markdown";

export default function Privacy() {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const markdownContent = `
# Privacy Policy

**Last Updated: February 26, 2026**

## 1. Introduction

This Privacy Policy describes how BStrong Performance ("we," "us," or "our") collects, uses, and shares information about you when you use our website and related services (collectively, the "Service").

**What We Do:** BStrong Performance is a professional leadership development and coaching firm. We provide executive coaching, leadership training, facilitation, and organizational development services to individuals and organizations.

**Age Requirement:** Our Service is intended for adults aged 18 years or older. We do not knowingly collect information from individuals under 18.

By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy.

## 2. Information We Collect

### 2.1 Information You Provide to Us

**Account and Contact Information:** When you create an account, register for a program, or contact us, we may collect:
- Full name
- Email address
- Phone number
- Organization or company name
- Job title or role
- Password (encrypted, if you register with email/password)

**Engagement Information:** In connection with coaching and training engagements, we may collect:
- Information you share during intake forms, needs assessments, or pre-engagement questionnaires
- Goals, challenges, and development areas you choose to share
- Assessment results and interpretations (e.g., MBTI, Hogan, EQ-i2.0, and other tools)
- Notes or materials you share with your coach or facilitator

**Payment Information:** Fees for services are invoiced directly. We do not store full payment card details. Payments processed online may be handled by a third-party payment processor (such as Stripe), which secures your payment data under its own privacy practices.

**Communications:** When you contact us by email, phone, or through our website, we collect:
- The content of your messages
- Any information you choose to provide

### 2.2 Information Collected Automatically

**Usage Information:**
- Pages visited and features used on our website
- Time and duration of visits
- Referring URLs and navigation paths

**Device and Technical Information:**
- Browser type and version
- Operating system
- IP address and general geographic location

**Cookies and Similar Technologies:**
- We use cookies and similar technologies to operate our website, remember your preferences, and analyze usage patterns. You may adjust cookie settings in your browser at any time.

## 3. How We Use Your Information

We use the information we collect to:

### 3.1 Provide and Deliver Our Services
- Set up and manage your account
- Deliver coaching, facilitation, training programs, and related services
- Administer and interpret assessments
- Communicate with you about your engagement
- Process payments and send invoices and receipts

### 3.2 Improve Our Services
- Analyze how our website and programs are used
- Develop new offerings and improve existing ones
- Conduct research and quality assurance

### 3.3 Communicate With You
- Respond to inquiries and provide customer support
- Send program updates, scheduling information, and materials
- Send promotional communications about our services (with your consent, where required)
- Send important policy or service updates

### 3.4 Safety, Security, and Legal Compliance
- Prevent fraud and abuse
- Comply with applicable legal obligations
- Enforce our Terms of Service
- Protect the rights and property of BStrong Performance and our clients

## 4. How We Share Your Information

**We do not sell your personal information.** We do not share your information with third parties for their own marketing purposes.

We may share your information only in the following limited circumstances:

### 4.1 Third-Party Service Providers

We work with trusted vendors who help us operate our business, including:

**Authentication & Infrastructure:**
- Firebase (Google) — user authentication and data storage

**Payment Processing:**
- Stripe — online payment processing (subject to Stripe's privacy policy)

**Analytics:**
- Google Analytics — website usage analysis

**Communication Tools:**
- Email and scheduling platforms used to coordinate engagements

These providers are contractually required to protect your information and use it only for the purposes we specify.

### 4.2 Sponsoring Organizations

If your coaching or training engagement is sponsored by your employer or another organization, we may share agreed-upon summary information (e.g., program completion status) as specified in your Engagement Agreement. We will not share the confidential content of your individual coaching sessions without your explicit consent.

### 4.3 Business Transfers

If BStrong Performance is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you before your information is transferred and becomes subject to a different privacy policy.

### 4.4 Legal Requirements

We may disclose your information when required by law or in response to:
- Court orders or subpoenas
- Government or regulatory requests
- Requests from law enforcement
- Protection of our legal rights
- Prevention of fraud or imminent harm

### 4.5 With Your Consent

We may share your information with third parties when you give us explicit permission to do so.

## 5. Coaching Confidentiality

BStrong Performance is committed to maintaining the confidentiality of information shared in coaching and facilitation engagements. Information disclosed in individual coaching sessions will not be shared with third parties — including sponsoring organizations — without your consent, except:
- As required by law or legal process
- Where there is risk of imminent harm to you or others

Please see your Engagement Agreement for any agreed-upon reporting arrangements with a sponsoring organization.

## 6. Third-Party Links and Services

Our website may contain links to third-party websites or resources. We are not responsible for the privacy practices of those third parties. We encourage you to review the privacy policies of any third-party sites you visit.

**Third-Party Authentication:** If you sign in using Google Sign-In or a similar service, that provider's privacy policy applies to the authentication process.

## 7. Data Retention

We retain your information for as long as necessary to:
- Maintain our business relationship with you
- Comply with legal and accounting obligations
- Resolve disputes and enforce our agreements

When you request deletion of your account or data, we will delete or anonymize your information within 90 days, except where retention is required by law or legitimate business necessity.

## 8. Your Privacy Rights

Depending on your location, you may have certain rights regarding your personal information:

### 8.1 Access and Portability
- Request a copy of the personal information we hold about you
- Receive your data in a portable format where technically feasible

### 8.2 Correction
- Request correction of inaccurate or incomplete information

### 8.3 Deletion
- Request deletion of your personal information
- Note: Some information may be retained as required by law or contractual obligation

### 8.4 Opt-Out Rights
- Unsubscribe from marketing emails at any time using the unsubscribe link in any email
- Opt out of certain cookies via your browser settings
- Request that we not use your information for marketing

### 8.5 California Privacy Rights (CCPA/CPRA)

California residents have additional rights, including:
- Right to know what personal information we collect and how it is used
- Right to know whether we sell or share personal information (we do not sell personal information)
- Right to request deletion of personal information
- Right to correct inaccurate personal information
- Right to non-discrimination for exercising your rights

### 8.6 European Privacy Rights (GDPR)

If you are located in the European Economic Area, UK, or Switzerland, you have rights including:
- Right to access your personal data
- Right to rectification of inaccurate data
- Right to erasure ("right to be forgotten")
- Right to restrict or object to processing
- Right to data portability
- Right to withdraw consent
- Right to lodge a complaint with a supervisory authority

### 8.7 Exercising Your Rights

To exercise any of these rights, contact us at brian@bstrongperformance.com with the subject line "Privacy Request." We will respond within the timeframe required by applicable law (generally 30–45 days).

## 9. International Data Transfers

If you are located outside the United States, your information may be transferred to and processed in the United States, where our operations are based. We take appropriate safeguards to ensure your information remains protected in accordance with this Privacy Policy.

## 10. Children's Privacy

Our Service is not directed to individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us immediately and we will delete it.

## 11. Security

We implement reasonable administrative, technical, and physical safeguards to protect your information, including:
- Encryption of data in transit (SSL/TLS)
- Access controls and authentication requirements
- Regular review of our data practices

No method of transmission or storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.

## 12. Do Not Track

Some browsers transmit "Do Not Track" signals. We currently do not alter our data collection practices in response to these signals, but we provide opt-out options as described in this Privacy Policy.

## 13. Changes to This Privacy Policy

We may update this Privacy Policy periodically. When we make material changes, we will:
- Update the "Last Updated" date at the top of this page
- Notify you via email or a notice on our website

Your continued use of the Service after changes take effect constitutes acceptance of the updated Privacy Policy.

## 14. Contact Us

If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:

**BStrong Performance**
Email: brian@BStrongPerformance.com

Please include "Privacy Request" in the subject line for any privacy-related inquiries or data rights requests.

---

**By using our Service, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.**`;

  return (
    <div>
      <PageNav />
      <div className="main_div">
        <div
          className="terms-container"
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            padding: "2rem",
            lineHeight: "1.6",
          }}
        >
          <ReactMarkdown
            components={{
              h1: ({ ...props }) => (
                <h1
                  className="font_family_base"
                  style={{
                    fontSize: "2.5rem",
                    marginBottom: "1rem",
                    borderBottom: "2px solid #737373",
                    paddingBottom: "0.5rem",
                  }}
                  {...props}
                />
              ),
              h2: ({ ...props }) => (
                <h2
                  className="font_family_base header_text"
                  style={{
                    fontSize: "1.8rem",
                    marginTop: "2rem",
                    marginBottom: "1rem",
                    // color: "#00bf62",
                  }}
                  {...props}
                />
              ),
              h3: ({ ...props }) => (
                <h3
                  className="font_family_base detail_text"
                  style={{
                    fontSize: "1.4rem",
                    marginTop: "1.5rem",
                    marginBottom: "0.75rem",
                  }}
                  {...props}
                />
              ),
              p: ({ ...props }) => (
                <p
                  className="font_family_base detail_text"
                  style={{ marginBottom: "1rem" }}
                  {...props}
                />
              ),
              ul: ({ ...props }) => (
                <ul
                  className="font_family_base detail_text"
                  style={{ marginLeft: "2rem", marginBottom: "1rem" }}
                  {...props}
                />
              ),
              li: ({ ...props }) => (
                <li
                  className="font_family_base detail_text"
                  style={{ marginBottom: "0.5rem" }}
                  {...props}
                />
              ),
              strong: ({ ...props }) => (
                <strong
                  className="font_family_base header_text"
                  // style={{ color: "#00bf62" }}
                  {...props}
                />
              ),
              hr: ({ ...props }) => (
                <hr
                  style={{
                    border: "none",
                    borderTop: "1px solid #737373",
                    margin: "2rem 0",
                  }}
                  {...props}
                />
              ),
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>
      </div>
      <Footer />
    </div>
  );
}
